import { DEFAULT_HANGING_PROTOCOLS } from '@/data/defaultHangingProtocols';
import { getAccessToken, getViewerSettings, putViewerSettings } from '@/lib/api';
import { sanitizeMonitorLayouts } from '@/lib/hangingProtocols/monitorSpans';
import { DEFAULT_KEYBOARD_SHORTCUTS } from '@/lib/shortcuts/shortcutActions';
import { DEFAULT_ECHO_MEASUREMENT_PANEL, DEFAULT_MODALITY_LAYOUTS, DEFAULT_OVERLAY_SETTINGS, DEFAULT_TOOL_BINDINGS, DEFAULT_WL_PRESETS, DEFAULT_WORKLIST_COLUMNS, VIEWER_SETTINGS_STORAGE_KEY, VIEWER_SETTINGS_VERSION, } from './defaults';
/**
 * Service for managing viewer settings with localStorage persistence
 */
class ViewerSettingsService {
    settings;
    listeners = new Set();
    syncTimer = null;
    syncInitialized = false;
    static SYNC_DEBOUNCE_MS = 1000;
    constructor() {
        this.settings = this.load();
    }
    /**
     * Load settings from localStorage or use defaults
     */
    load() {
        try {
            const stored = localStorage.getItem(VIEWER_SETTINGS_STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                return this.mergeWithDefaults(parsed);
            }
        }
        catch (error) {
            console.warn('[ViewerSettings] Failed to load from localStorage:', error);
        }
        return this.getDefaultSettings();
    }
    /**
     * Get default settings
     */
    getDefaultSettings() {
        return {
            version: VIEWER_SETTINGS_VERSION,
            modalityLayouts: [...DEFAULT_MODALITY_LAYOUTS],
            toolBindings: { ...DEFAULT_TOOL_BINDINGS },
            overlay: { ...DEFAULT_OVERLAY_SETTINGS },
            wlPresets: [...DEFAULT_WL_PRESETS],
            modalityOverlays: {},
            echoMeasurementPanel: { ...DEFAULT_ECHO_MEASUREMENT_PANEL },
            studiesWorklist: { columns: [...DEFAULT_WORKLIST_COLUMNS] },
            hangingProtocols: structuredClone(DEFAULT_HANGING_PROTOCOLS),
            hangingProtocolsEnabled: true,
            keyboardShortcuts: { ...DEFAULT_KEYBOARD_SHORTCUTS },
            audioInput: { deviceId: null },
            seriesStripPosition: 'top',
            mprViewConfig: 'quad',
        };
    }
    mergeWorklistColumns(storedColumns) {
        if (!storedColumns?.length)
            return [...DEFAULT_WORKLIST_COLUMNS];
        const defaultKeys = new Set(DEFAULT_WORKLIST_COLUMNS.map((c) => c.key));
        const seen = new Set();
        const merged = [];
        for (const col of storedColumns) {
            if (!defaultKeys.has(col.key) || seen.has(col.key))
                continue;
            merged.push({ key: col.key, visible: col.visible !== false });
            seen.add(col.key);
        }
        for (const def of DEFAULT_WORKLIST_COLUMNS) {
            if (seen.has(def.key))
                continue;
            merged.push({ ...def });
        }
        if (!merged.some((c) => c.visible)) {
            merged[0] = { ...merged[0], visible: true };
        }
        return merged;
    }
    /**
     * Merge stored settings with defaults to handle new fields/modalities
     */
    mergeWithDefaults(stored) {
        const defaults = this.getDefaultSettings();
        // Use stored modality layouts directly if they exist, otherwise use defaults
        // This ensures user customizations are preserved
        const modalityLayouts = stored.modalityLayouts?.length
            ? stored.modalityLayouts
            : defaults.modalityLayouts;
        // Merge W/L presets (keep custom presets from storage)
        const customPresets = stored.wlPresets?.filter((p) => p.isCustom) || [];
        const mergedPresets = [
            ...DEFAULT_WL_PRESETS.map((defaultPreset) => {
                const storedPreset = stored.wlPresets?.find((p) => p.id === defaultPreset.id);
                return storedPreset
                    ? { ...defaultPreset, isFavorite: storedPreset.isFavorite }
                    : defaultPreset;
            }),
            ...customPresets,
        ];
        // Sanitize tool bindings at the merge boundary — this is the single point
        // every source flows through (localStorage AND the backend-synced copy,
        // which initFromBackend treats as source of truth). A corrupted map that
        // reaches the server otherwise re-infects every browser that logs in
        // forever: observed live as left AND right both bound to the Arrow
        // annotation tool — both mouse buttons drawing arrows, W/L and Zoom gone,
        // "totally broke clicking" — surviving a code rollback because the DATA
        // was never healed. Rule: the same tool must never own two buttons; the
        // later button in left→right→middle order resets to its default.
        const mergedBindings = { ...defaults.toolBindings, ...stored.toolBindings };
        const seenTools = new Set();
        for (const key of ['left', 'right', 'middle']) {
            const tool = mergedBindings[key];
            if (typeof tool !== 'string')
                continue;
            if (seenTools.has(tool)) {
                // Default first; if the default is ALSO taken (stored left=Zoom,
                // right=Zoom, default right=Zoom), walk the nav-tool fallbacks.
                const candidates = [defaults.toolBindings[key], 'WindowLevel', 'Zoom', 'Pan'];
                const replacement = candidates.find((c) => typeof c === 'string' && !seenTools.has(c));
                if (replacement) {
                    mergedBindings[key] = replacement;
                    seenTools.add(replacement);
                }
            }
            else {
                seenTools.add(tool);
            }
        }
        return {
            version: VIEWER_SETTINGS_VERSION,
            modalityLayouts,
            toolBindings: mergedBindings,
            overlay: { ...defaults.overlay, ...stored.overlay },
            wlPresets: mergedPresets,
            modalityOverlays: stored.modalityOverlays ?? {},
            echoMeasurementPanel: this.sanitizeEchoMeasurementPanel({
                ...defaults.echoMeasurementPanel,
                ...stored.echoMeasurementPanel,
            }),
            studiesWorklist: {
                columns: this.mergeWorklistColumns(stored.studiesWorklist?.columns),
            },
            // Stored protocols win wholesale, but their optional multi-monitor spans
            // are sanitized here — the single choke point — so a malformed or
            // corrupted monitorLayouts entry degrades to a single-window protocol
            // instead of taking the viewer's apply effect down everywhere the blob
            // syncs to.
            hangingProtocols: stored.hangingProtocols?.length
                ? stored.hangingProtocols.map((protocol) => {
                    const spans = sanitizeMonitorLayouts(protocol.monitorLayouts);
                    if (spans)
                        return { ...protocol, monitorLayouts: spans };
                    if ('monitorLayouts' in protocol) {
                        const { monitorLayouts: _dropped, ...rest } = protocol;
                        return rest;
                    }
                    return protocol;
                })
                : defaults.hangingProtocols,
            hangingProtocolsEnabled: stored.hangingProtocolsEnabled ?? defaults.hangingProtocolsEnabled ?? true,
            // Merge so newly-added actions always have a default binding.
            keyboardShortcuts: { ...DEFAULT_KEYBOARD_SHORTCUTS, ...stored.keyboardShortcuts },
            audioInput: stored.audioInput ?? defaults.audioInput,
            seriesStripPosition: stored.seriesStripPosition ?? defaults.seriesStripPosition,
            mprViewConfig: stored.mprViewConfig ?? defaults.mprViewConfig,
        };
    }
    /**
     * The echo panel reads its Standard | Extended view and collapsed groups
     * straight off this blob, which the backend stores as opaque JSON, so an
     * older or hand-edited copy must reach the panel as one of its own values:
     * anything but 'extended' reads as Standard, and the collapsed groups keep
     * only distinct string ids.
     */
    sanitizeEchoMeasurementPanel(panel) {
        const collapsedGroups = Array.isArray(panel.collapsedGroups)
            ? [...new Set(panel.collapsedGroups.filter((id) => typeof id === 'string'))]
            : [];
        return {
            ...panel,
            tierView: panel.tierView === 'extended' ? 'extended' : 'standard',
            collapsedGroups,
            extendedGroupsSeeded: panel.extendedGroupsSeeded === true,
        };
    }
    /**
     * Save settings to localStorage and notify listeners, then sync to backend
     */
    save() {
        this.saveToLocalStorage();
        this.notifyListeners();
        this.scheduleSyncToBackend();
    }
    saveToLocalStorage() {
        try {
            localStorage.setItem(VIEWER_SETTINGS_STORAGE_KEY, JSON.stringify(this.settings));
        }
        catch (error) {
            console.warn('[ViewerSettings] Failed to save to localStorage:', error);
        }
    }
    scheduleSyncToBackend() {
        if (!this.syncInitialized)
            return;
        if (this.syncTimer)
            clearTimeout(this.syncTimer);
        this.syncTimer = setTimeout(() => {
            this.syncToBackendNow();
        }, ViewerSettingsService.SYNC_DEBOUNCE_MS);
    }
    syncToBackendNow() {
        putViewerSettings(JSON.stringify(this.settings)).catch((error) => {
            console.warn('[ViewerSettings] Failed to sync to backend:', error);
        });
    }
    /**
     * Initialize settings from backend after authentication.
     * If backend has settings, they overwrite localStorage.
     * If backend has no settings (first login), push localStorage to backend.
     */
    async initFromBackend() {
        if (this.syncInitialized)
            return;
        if (!getAccessToken())
            return;
        try {
            const result = await getViewerSettings();
            if (result) {
                // Backend has settings — use them as source of truth
                const parsed = JSON.parse(result.settings);
                this.settings = this.mergeWithDefaults(parsed);
                this.saveToLocalStorage();
                this.notifyListeners();
            }
            else {
                // No backend settings — migrate current localStorage settings to backend
                this.syncToBackendNow();
            }
        }
        catch (error) {
            console.warn('[ViewerSettings] Failed to init from backend, using localStorage:', error);
        }
        this.syncInitialized = true;
    }
    /**
     * Reset state on logout
     */
    resetForLogout() {
        this.syncInitialized = false;
        if (this.syncTimer) {
            clearTimeout(this.syncTimer);
            this.syncTimer = null;
        }
        this.settings = this.getDefaultSettings();
        this.saveToLocalStorage();
        this.notifyListeners();
    }
    /**
     * Subscribe to settings changes
     */
    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
    /**
     * Notify all listeners of changes
     */
    notifyListeners() {
        this.listeners.forEach((listener) => listener());
    }
    // ============================================================================
    // General
    // ============================================================================
    /**
     * Get all settings
     */
    getSettings() {
        return {
            ...this.settings,
            modalityLayouts: [...this.settings.modalityLayouts],
            toolBindings: { ...this.settings.toolBindings },
            overlay: { ...this.settings.overlay },
            wlPresets: [...this.settings.wlPresets],
            modalityOverlays: { ...this.settings.modalityOverlays },
            studiesWorklist: this.settings.studiesWorklist
                ? { columns: [...this.settings.studiesWorklist.columns] }
                : undefined,
            hangingProtocols: this.settings.hangingProtocols
                ? structuredClone(this.settings.hangingProtocols)
                : undefined,
            keyboardShortcuts: { ...this.settings.keyboardShortcuts },
            audioInput: this.settings.audioInput ? { ...this.settings.audioInput } : undefined,
        };
    }
    /**
     * Update multiple settings at once
     */
    updateSettings(updates) {
        if (updates.modalityLayouts) {
            this.settings.modalityLayouts = updates.modalityLayouts;
        }
        if (updates.toolBindings) {
            this.settings.toolBindings = { ...this.settings.toolBindings, ...updates.toolBindings };
        }
        if (updates.overlay) {
            this.settings.overlay = { ...this.settings.overlay, ...updates.overlay };
        }
        if (updates.wlPresets) {
            this.settings.wlPresets = updates.wlPresets;
        }
        if (updates.studiesWorklist?.columns) {
            this.settings.studiesWorklist = {
                columns: this.mergeWorklistColumns(updates.studiesWorklist.columns),
            };
        }
        if (updates.hangingProtocols) {
            this.settings.hangingProtocols = updates.hangingProtocols;
        }
        if (updates.hangingProtocolsEnabled !== undefined) {
            this.settings.hangingProtocolsEnabled = updates.hangingProtocolsEnabled;
        }
        if (updates.keyboardShortcuts) {
            this.settings.keyboardShortcuts = {
                ...this.settings.keyboardShortcuts,
                ...updates.keyboardShortcuts,
            };
        }
        if (updates.audioInput) {
            this.settings.audioInput = { ...this.settings.audioInput, ...updates.audioInput };
        }
        if (updates.seriesStripPosition) {
            this.settings.seriesStripPosition = updates.seriesStripPosition;
        }
        if (updates.mprViewConfig) {
            this.settings.mprViewConfig = updates.mprViewConfig;
        }
        this.save();
    }
    /**
     * Reset all settings to defaults
     */
    resetToDefaults() {
        this.settings = this.getDefaultSettings();
        this.save();
    }
    // ============================================================================
    // Modality Layouts
    // ============================================================================
    /**
     * Get the layout for a specific modality
     */
    getLayoutForModality(modality) {
        const found = this.settings.modalityLayouts.find((m) => m.modality.toUpperCase() === modality.toUpperCase());
        return found?.layout || { rows: 1, cols: 1 };
    }
    /**
     * Set the layout for a specific modality
     */
    setModalityLayout(modality, layout) {
        const updated = this.settings.modalityLayouts.map((ml) => ml.modality.toUpperCase() === modality.toUpperCase() ? { ...ml, layout } : ml);
        this.settings.modalityLayouts = updated;
        this.save();
    }
    // ============================================================================
    // Tool Bindings
    // ============================================================================
    /**
     * Get tool bindings
     */
    getToolBindings() {
        return { ...this.settings.toolBindings };
    }
    /**
     * Update tool bindings
     */
    updateToolBindings(bindings) {
        this.settings.toolBindings = { ...this.settings.toolBindings, ...bindings };
        this.save();
    }
    // ============================================================================
    // Overlay Settings
    // ============================================================================
    /**
     * Get overlay settings, optionally merged with per-modality overrides.
     */
    getOverlaySettings(modality) {
        const base = { ...this.settings.overlay };
        if (modality && this.settings.modalityOverlays?.[modality]) {
            return { ...base, ...this.settings.modalityOverlays[modality] };
        }
        return base;
    }
    /**
     * Update overlay settings. If modality is provided, saves to that modality's overrides only.
     */
    updateOverlaySettings(overlay, modality) {
        if (modality) {
            const current = this.settings.modalityOverlays ?? {};
            current[modality] = { ...(current[modality] ?? {}), ...overlay };
            this.settings.modalityOverlays = current;
        }
        else {
            this.settings.overlay = { ...this.settings.overlay, ...overlay };
        }
        this.save();
    }
    /**
     * Get the list of modalities that have custom overlay overrides.
     */
    getModalityOverlayKeys() {
        return Object.keys(this.settings.modalityOverlays ?? {});
    }
    /**
     * Remove all per-modality overlay overrides for a given modality (falls back to default).
     */
    clearModalityOverlay(modality) {
        if (this.settings.modalityOverlays) {
            delete this.settings.modalityOverlays[modality];
            this.save();
        }
    }
    // ============================================================================
    // W/L Presets
    // ============================================================================
    /**
     * Get all W/L presets
     */
    getWLPresets() {
        return [...this.settings.wlPresets];
    }
    /**
     * Get favorite presets for toolbar
     */
    getFavoritePresets() {
        return this.settings.wlPresets.filter((p) => p.isFavorite);
    }
    /**
     * Get presets for a specific modality
     */
    getPresetsForModality(modality) {
        return this.settings.wlPresets.filter((p) => !p.modality || p.modality.toUpperCase() === modality.toUpperCase());
    }
    /**
     * Toggle preset favorite status
     */
    togglePresetFavorite(id) {
        const preset = this.settings.wlPresets.find((p) => p.id === id);
        if (preset) {
            preset.isFavorite = !preset.isFavorite;
            this.save();
        }
    }
    /**
     * Add a custom preset
     */
    addCustomPreset(preset) {
        this.settings.wlPresets.push({
            ...preset,
            id: `custom-${Date.now()}`,
            isCustom: true,
        });
        this.save();
    }
    /**
     * Remove a custom preset
     */
    removeCustomPreset(id) {
        this.settings.wlPresets = this.settings.wlPresets.filter((p) => p.id !== id || !p.isCustom);
        this.save();
    }
    /**
     * Update a custom preset
     */
    updateCustomPreset(id, updates) {
        const preset = this.settings.wlPresets.find((p) => p.id === id && p.isCustom);
        if (preset) {
            Object.assign(preset, updates);
            this.save();
        }
    }
    // ============================================================================
    // Echo Measurement Panel
    // ============================================================================
    /**
     * Get the persisted echo-panel settings, merged with defaults. Guaranteed
     * to return a fully-populated object even on first use.
     */
    getEchoMeasurementPanel() {
        return {
            ...DEFAULT_ECHO_MEASUREMENT_PANEL,
            ...(this.settings.echoMeasurementPanel ?? {}),
        };
    }
    /**
     * Update one or more fields of the echo panel settings. Position writes
     * are high-frequency during drag; the debounced save in `save()` keeps
     * network churn to one request per pause.
     */
    updateEchoMeasurementPanel(updates) {
        this.settings.echoMeasurementPanel = {
            ...this.getEchoMeasurementPanel(),
            ...updates,
        };
        this.save();
    }
    /**
     * Get the default W/L preset for a modality
     * Prioritizes favorites, then falls back to any preset for the modality
     */
    getDefaultWLPresetForModality(modality) {
        const upperModality = modality.toUpperCase();
        // First try to find a favorite preset for this modality
        const favoritePreset = this.settings.wlPresets.find((p) => p.modality?.toUpperCase() === upperModality && p.isFavorite);
        if (favoritePreset)
            return favoritePreset;
        // Fall back to first preset for the modality
        const modalityPreset = this.settings.wlPresets.find((p) => p.modality?.toUpperCase() === upperModality);
        if (modalityPreset)
            return modalityPreset;
        // For X-ray modalities, try CR preset
        if (upperModality === 'DX') {
            const crPreset = this.settings.wlPresets.find((p) => p.modality?.toUpperCase() === 'CR');
            if (crPreset)
                return crPreset;
        }
        return null;
    }
    // ============================================================================
    // Keyboard Shortcuts
    // ============================================================================
    /** Get the effective shortcut map (defaults merged with user overrides). */
    getKeyboardShortcuts() {
        return { ...DEFAULT_KEYBOARD_SHORTCUTS, ...this.settings.keyboardShortcuts };
    }
    /** Update one or more shortcut bindings (action id → chord). */
    updateKeyboardShortcuts(updates) {
        this.settings.keyboardShortcuts = { ...this.settings.keyboardShortcuts, ...updates };
        this.save();
    }
    /** Reset all shortcuts back to their defaults. */
    resetKeyboardShortcuts() {
        this.settings.keyboardShortcuts = { ...DEFAULT_KEYBOARD_SHORTCUTS };
        this.save();
    }
    // ============================================================================
    // Audio Input (microphone)
    // ============================================================================
    getAudioInput() {
        return { deviceId: null, ...this.settings.audioInput };
    }
    updateAudioInput(updates) {
        this.settings.audioInput = { ...this.getAudioInput(), ...updates };
        this.save();
    }
}
// Export singleton instance
export const viewerSettingsService = new ViewerSettingsService();
